import qz from "./qz";
import dateFns from "date-fns";
import QRCode from "qrcode";
import { apiHelper } from "./helpers";
import endpoints from "@/utils/constants";

const isDevelopment =
  process.env.NODE_ENV === "development" ||
  window.location.hostname === "localhost";
const forcePreview =
  new URLSearchParams(window.location.search).get("preview") === "true";

// ─── Branch lookup ──────────────────────────────────────────────────────────

const fetchBranchDetails = async branchName => {
  try {
    const res = await apiHelper.call(
      "get",
      `${endpoints.LARAVEL_BASE_URL}/api/v1/branches`
    );
    const branches = (res.data && res.data.data) || [];
    if (!branchName) {
      return branches.find(b => b.is_default) || branches[0] || null;
    }
    const key = branchName.toString().toLowerCase().trim();
    return (
      branches.find(b => b.id === branchName) ||
      branches.find(b => b.name && b.name.toLowerCase().trim() === key) ||
      branches.find(
        b => b.legal_name && b.legal_name.toLowerCase().trim() === key
      ) ||
      null
    );
  } catch {
    return null;
  }
};

// Split address at the comma nearest the midpoint so it fits on two lines
const splitAddress = addr => {
  if (!addr) return ["", ""];
  const mid = Math.floor(addr.length / 2);
  let bestIdx = -1;
  let bestDist = Infinity;
  for (let i = 0; i < addr.length; i++) {
    if (addr[i] === ",") {
      const d = Math.abs(i - mid);
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    }
  }
  if (bestIdx === -1) return [addr, ""];
  return [addr.slice(0, bestIdx).trim(), addr.slice(bestIdx + 1).trim()];
};

// ─── ESC/POS QR code helper ─────────────────────────────────────────────────

const escPosQR = str => {
  const storeLen = str.length + 3;
  const pL = String.fromCharCode(storeLen & 0xff);
  const pH = String.fromCharCode((storeLen >> 8) & 0xff);
  return [
    "\x1d\x28\x6b\x04\x00\x31\x41\x32\x00", // model 2
    "\x1d\x28\x6b\x03\x00\x31\x43\x05",       // module size 5
    "\x1d\x28\x6b\x03\x00\x31\x45\x31",       // error correction M
    "\x1d\x28\x6b" + pL + pH + "\x31\x50\x30" + str, // store data
    "\x1d\x28\x6b\x03\x00\x31\x51\x30"        // print
  ];
};

// ─── Data preparation ────────────────────────────────────────────────────────

const prepareData = (data, type) => {
  if (type === "sale") {
    const products = data.productList.map(i => ({
      name: i.name,
      qty: i.quantity,
      rate: i.rate,
      total: i.amount
    }));
    const meta = {
      subTotal: data.finalTotal,
      grandTotal: data.grandTotal,
      date: dateFns.format(new Date(data.date), "DD/MM/YYYY"),
      name: `${data.userData.firstName} ${data.userData.lastName}`,
      phone: data.userData.phone || "",
      discount: data.isPercentage
        ? `${data.discount} %`
        : `Rs. ${data.discount}`,
      roundOff: data.roundOff || 0
    };
    return { products, meta };
  } else if (type === "chalan") {
    const products = data.chalanItems.map(i => ({
      name: i.name,
      qty: i.quantity,
      rate: i.price,
      total: i.total
    }));
    const meta = {
      subTotal: data.subTotal,
      roundOff: data.roundedOff,
      grandTotal: data.grandTotal,
      date: dateFns.format(new Date(data.updatedAt), "DD/MM/YYYY"),
      name: `${data.users.firstName} ${data.users.lastName}`,
      phone: data.users.phone,
      vendor: `${data.vendor.firstName} ${data.vendor.lastName}`,
      chalanNumber: data.chalanNumber,
      chalanType: data.isInward ? "Inward" : "Outward"
    };
    return { products, meta };
  } else if (type === "quotation") {
    const products = data.products.map(i => ({
      name: i.name,
      qty: i.quantity,
      rate: i.price,
      total: i.amount
    }));
    const meta = {
      subTotal: data.finalTotal,
      grandTotal: data.grandTotal,
      date: dateFns.format(new Date(data.updatedAt), "DD/MM/YYYY"),
      name: `${data.user.firstName} ${data.user.lastName}`,
      phone: data.user.phone
    };
    return { products, meta };
  } else if (type === "sale-quotation") {
    const products = data.productMeta.map(i => ({
      name: i.name,
      qty: i.quantity,
      rate: i.price,
      total: i.quantity * i.price
    }));
    const meta = {
      subTotal: data.total,
      grandTotal: data.total,
      date: dateFns.format(new Date(data.createdAt), "DD/MM/YYYY"),
      name: `${data.quotationUser.firstName} ${data.quotationUser.lastName}`,
      phone: data.quotationUser.phone
    };
    return { products, meta };
  } else if (type === "customer-chalan") {
    const products = data.products.map(i => ({
      name: i.name,
      qty: i.quantity,
      rate: i.price,
      total: i.total
    }));
    const meta = {
      subTotal: data.subTotal,
      grandTotal: data.grandTotal,
      roundOff: data.roundedOff,
      date: dateFns.format(new Date(data.createdAt), "DD/MM/YYYY"),
      name: data.user,
      phone: data.user.phone,
      chalanNumber: data.chalanNumber,
      chalanType: data.is_inward ? "Inward" : "Outward"
    };
    return { products, meta };
  }
};

// ─── HTML preview ────────────────────────────────────────────────────────────

const generateHTMLPreview = (products, meta, type) => {
  const companyName = meta.companyName || "Rajput Electricals";
  const [addrLine1, addrLine2] = splitAddress(meta.companyAddress || "");
  const phone = meta.companyPhone || "";

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Print Preview - ${type}</title>
      <style>
        body {
          font-family: 'Courier New', monospace;
          width: 300px;
          margin: 0 auto;
          padding: 20px;
          background: white;
          color: black;
        }
        .company-name { font-size: 20px; font-weight: bold; margin-bottom: 5px; text-align: center; }
        .address { font-size: 12px; line-height: 1.4; margin-bottom: 10px; text-align: center; }
        .date { text-align: left; margin-bottom: 10px; }
        .separator-line { border-top: 1px dashed black; margin: 10px 0; }
        .customer-info { margin-bottom: 10px; }
        .chalan-info { margin-bottom: 10px; }
        .table-header {
          font-weight: bold; margin-bottom: 5px;
          display: grid; grid-template-columns: 1fr 4fr 2fr 2fr 2fr;
        }
        .product-row {
          display: grid; grid-template-columns: 1fr 4fr 2fr 2fr 2fr;
          align-items: baseline; margin-bottom: 3px; font-size: 12px;
        }
        .totals { margin-top: 10px; font-weight: bold; text-align: right; }
        .total-row { margin-bottom: 3px; }
        .upi-section { margin-top: 15px; text-align: center; }
        .upi-section img { width: 120px; height: 120px; display: block; margin: 0 auto; }
        .upi-section p { font-size: 11px; margin: 4px 0 0; }
        .print-footer { margin-top: 20px; text-align: center; font-size: 10px; }
        @media print { body { margin: 0; padding: 10px; } }
      </style>
    </head>
    <body>
      <div class="company-name">${companyName}</div>
      <div class="address">
        ${addrLine1}${addrLine2 ? "<br>" + addrLine2 : ""}
        ${phone ? "<br>Contact: " + phone : ""}
      </div>

      <div class="date">${meta.date}</div>
      <div class="separator-line"></div>

      <div class="customer-info">
        <strong>Customer:</strong> ${meta.name}<br>
        ${meta.phone ? `<strong>Phone:</strong> ${meta.phone}<br>` : ""}
      </div>

      ${type === "chalan" ? `<div><strong>Vendor:</strong> ${meta.vendor}<br></div>` : ""}
      ${type === "customer-chalan" || type === "chalan" ? `
        <div class="chalan-info">
          <strong>Chalan:</strong> ${meta.chalanNumber} (${meta.chalanType})
        </div>` : ""}

      <div class="separator-line"></div>

      <div class="table-header">
        <span>#</span><span>Item</span><span>Qty</span><span>Rate</span><span>Total</span>
      </div>

      ${products.map((item, idx) => `
        <div class="product-row">
          <span>${idx + 1}</span>
          <span>${item.name}</span>
          <span>${item.qty}</span>
          <span>${item.rate}</span>
          <span>${item.total}</span>
        </div>`).join("")}

      <div class="separator-line"></div>

      <div class="totals">
        <div class="total-row">Subtotal: ${meta.subTotal}</div>
        ${meta.discount ? `<div class="total-row">Discount: ${meta.discount}</div>` : ""}
        ${meta.roundOff ? `<div class="total-row">Round Off: ${meta.roundOff}</div>` : ""}
        <div class="total-row">Total: ${Math.round(meta.grandTotal)}</div>
      </div>

      ${meta.upiQrDataUrl ? `
        <div class="upi-section">
          <img src="${meta.upiQrDataUrl}" alt="UPI QR" />
          <p>Scan to Pay via UPI</p>
          ${meta.upiId ? `<p>${meta.upiId}</p>` : ""}
        </div>` : ""}

      <div class="print-footer">
        <p>Thank you for your business!</p>
      </div>
    </body>
    </html>
  `;
  return html;
};

// ─── Preview modal ───────────────────────────────────────────────────────────

const showPreview = html => {
  const modalOverlay = document.createElement("div");
  modalOverlay.style.cssText = `
    position:fixed;top:0;left:0;width:100%;height:100%;
    background:rgba(0,0,0,0.5);z-index:9999;
    display:flex;justify-content:center;align-items:center;padding:20px;
  `;

  const modalContent = document.createElement("div");
  modalContent.style.cssText = `
    background:white;border-radius:8px;box-shadow:0 4px 20px rgba(0,0,0,0.3);
    max-width:90%;max-height:98%;overflow:auto;position:relative;
  `;

  const modalHeader = document.createElement("div");
  modalHeader.style.cssText = `
    padding:15px 20px;border-bottom:1px solid #e9ecef;
    display:flex;justify-content:space-between;align-items:center;
    background:#f8f9fa;border-radius:8px 8px 0 0;
  `;

  const modalTitle = document.createElement("h3");
  modalTitle.textContent = "Print Preview";
  modalTitle.style.cssText = "margin:0;font-size:18px;font-weight:600;color:#333;";

  const closeButton = document.createElement("button");
  closeButton.innerHTML = "&times;";
  closeButton.style.cssText = `
    background:none;border:none;font-size:24px;cursor:pointer;color:#666;
    padding:0;width:30px;height:30px;display:flex;align-items:center;
    justify-content:center;border-radius:50%;
  `;

  const modalBody = document.createElement("div");
  modalBody.style.cssText = "padding:20px;max-height:70vh;overflow-y:auto;";

  const modalFooter = document.createElement("div");
  modalFooter.style.cssText = `
    padding:15px 20px;border-top:1px solid #e9ecef;
    display:flex;justify-content:flex-end;gap:10px;
    background:#f8f9fa;border-radius:0 0 8px 8px;
  `;

  const printButton = document.createElement("button");
  printButton.innerHTML = isDevelopment || forcePreview ? "Print Preview" : "Print to Thermal Printer";
  printButton.style.cssText = `
    padding:10px 20px;background:#007bff;color:white;border:none;
    border-radius:5px;cursor:pointer;font-size:14px;font-weight:500;
  `;

  const closeModalButton = document.createElement("button");
  closeModalButton.innerHTML = "Close";
  closeModalButton.style.cssText = `
    padding:10px 20px;background:#6c757d;color:white;border:none;
    border-radius:5px;cursor:pointer;font-size:14px;font-weight:500;
  `;

  const closeModal = () => {
    document.body.removeChild(modalOverlay);
    document.removeEventListener("keydown", handleEscapeKey);
  };
  const handleEscapeKey = e => { if (e.key === "Escape") closeModal(); };

  closeButton.onclick = closeModal;
  closeModalButton.onclick = closeModal;
  printButton.onclick = () => {
    const printWindow = window.open("", "_blank", "width=400,height=600");
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  modalHeader.appendChild(modalTitle);
  modalHeader.appendChild(closeButton);
  modalFooter.appendChild(closeModalButton);
  modalFooter.appendChild(printButton);
  modalContent.appendChild(modalHeader);
  modalContent.appendChild(modalBody);
  modalContent.appendChild(modalFooter);
  modalOverlay.appendChild(modalContent);
  modalBody.innerHTML = html;
  document.body.appendChild(modalOverlay);
  document.addEventListener("keydown", handleEscapeKey);
  modalContent.focus();
};

// ─── Thermal ESC/POS print ───────────────────────────────────────────────────

const proceedWithThermalPrint = (products, meta, type) => {
  const companyName = meta.companyName || "Rajput Electricals";
  const [addrLine1, addrLine2] = splitAddress(meta.companyAddress || "");
  const phone = meta.companyPhone || "";

  const printWithConfig = printerName => {
    const config = qz.configs.create(printerName);
    const data = [
      "\x1B\x40",         // init
      "\x1B\x61\x31",     // center
      "\x1B\x45\x0D",     // bold on
      "\x1b\x21\x01\x1b\x21\x11\x1b\x21\x20",
      companyName + "\x0A",
      "\x1b\x21\x00",
      "\x1B\x45\x0A",     // bold off
      ...(addrLine1 ? [addrLine1 + "\x0A"] : []),
      ...(addrLine2 ? [addrLine2 + "\x0A"] : []),
      ...(phone ? ["Contact: " + phone + "\x0A"] : []),
      "\x1B\x61\x30\x0A", // left
      meta.date + "\x0A",
      "------------------------------------------------\x0A",
      "Customer: " + meta.name + "\x0A",
      ...(meta.phone ? ["Phone: " + meta.phone + "\x0A"] : []),
      ...(type === "customer-chalan" || type === "chalan"
        ? [
            "Chalan: " + meta.chalanNumber + " (" + meta.chalanType + ")\x0A"
          ]
        : []),
      ...(type === "chalan" ? ["Vendor: " + meta.vendor + "\x0A"] : []),
      "------------------------------------------------\x0A",
      "#  Item                    Qty   Rate    Total\x0A"
    ];

    const colWidths = { item: 20, qty: 4, rate: 8, total: 8 };
    products.forEach((item, index) => {
      const num = (index + 1).toString();
      const name = item.name;
      const qty = item.qty.toString();
      const rate = item.rate.toString();
      const total = item.total.toString();

      if (name.length > colWidths.item) {
        data.push(
          num.padEnd(3) +
            name.substr(0, colWidths.item) +
            qty.padStart(colWidths.qty) +
            rate.padStart(colWidths.rate) +
            total.padStart(colWidths.total) +
            "\x0A"
        );
        let remaining = name.substr(colWidths.item);
        while (remaining.length > 0) {
          data.push(
            "   " +
              remaining
                .substr(0, colWidths.item)
                .padEnd(
                  colWidths.item + colWidths.qty + colWidths.rate + colWidths.total
                ) +
              "\x0A"
          );
          remaining = remaining.substr(colWidths.item);
        }
      } else {
        const totalWidth =
          colWidths.item + colWidths.qty + colWidths.rate + colWidths.total;
        data.push(
          num.padEnd(3) +
            name.padEnd(colWidths.item) +
            qty.padStart(colWidths.qty) +
            rate.padStart(colWidths.rate) +
            total.padStart(colWidths.total) +
            "\x0A"
        );
      }
    });

    data.push("------------------------------------------------\x0A");
    data.push("\x1B\x45\x0D"); // bold
    const tw = colWidths.item + colWidths.qty + colWidths.rate + colWidths.total;
    data.push("Subtotal:".padEnd(tw - meta.subTotal.toString().length) + meta.subTotal + "\x0A");
    if (meta.discount) {
      data.push("Discount:".padEnd(tw - meta.discount.toString().length) + meta.discount + "\x0A");
    }
    if (meta.roundOff) {
      data.push("Round Off:".padEnd(tw - meta.roundOff.toString().length) + meta.roundOff + "\x0A");
    }
    const grandTotalRounded = Math.round(meta.grandTotal);
    data.push("Total:".padEnd(tw - grandTotalRounded.toString().length) + grandTotalRounded + "\x0A");
    data.push("\x1B\x45\x0A"); // bold off

    // UPI QR code
    if (meta.upiId && meta.grandTotal) {
      const upiStr = `upi://pay?pa=${meta.upiId}&pn=${encodeURIComponent(companyName)}&am=${meta.grandTotal}&cu=INR`;
      data.push("\x0A");
      data.push("\x1B\x61\x31"); // center
      data.push(...escPosQR(upiStr));
      data.push("Scan to Pay via UPI\x0A");
      data.push(meta.upiId + "\x0A");
      data.push("\x1B\x61\x30"); // left
    }

    data.push("\x0A\x0A\x0A\x0A\x0A\x0A\x0A");
    data.push("\x1B\x69");
    data.push("\x10\x14\x01\x00\x05");

    const doPrint = () => {
      qz.print(config, data)
        .then(() => console.log("Sent data to printer"))
        .catch(e => console.error(e));
    };

    if (qz.websocket.isActive()) {
      doPrint();
    } else {
      qz.websocket
        .connect()
        .then(doPrint)
        .catch(e => console.error(e));
    }
  };

  const runWithPrinter = () => {
    qz.printers
      .getDefault()
      .then(printer => printWithConfig(printer))
      .catch(e => {
        console.error(e);
        alert("Error getting default printer. Please check your printer settings.");
      });
  };

  if (qz.websocket.isActive()) {
    runWithPrinter();
  } else {
    qz.websocket.connect().then(runWithPrinter).catch(e => console.error(e));
  }
};

// ─── Public API ──────────────────────────────────────────────────────────────

export async function print(sale, type = "sale", company = null) {
  const { products, meta } = prepareData(sale, type);

  // Fetch dynamic branch details from the sale's branchName
  const branchName = (sale && sale.branchName) || null;
  const branch = await fetchBranchDetails(branchName);
  if (branch) {
    meta.companyName = branch.legal_name || branch.name || "Rajput Electricals";
    meta.companyAddress = branch.address || "";
    meta.companyPhone = branch.phone || "";
    meta.upiId = branch.upi_id || "";
  } else {
    // Fall back to explicitly-passed company name (Pos.vue passes sellingBranchLegalName)
    meta.companyName = company || "Rajput Electricals";
    meta.companyAddress = "";
    meta.companyPhone = "";
    meta.upiId = "";
  }

  // Generate UPI QR for HTML preview
  if (meta.upiId && meta.grandTotal) {
    const upiStr = `upi://pay?pa=${meta.upiId}&pn=${encodeURIComponent(meta.companyName)}&am=${Math.round(meta.grandTotal)}&cu=INR`;
    try {
      meta.upiQrDataUrl = await QRCode.toDataURL(upiStr, { width: 200, margin: 1 });
    } catch {
      meta.upiQrDataUrl = null;
    }
  }

  const html = generateHTMLPreview(products, meta, type);
  showPreview(html);

  if (isDevelopment || forcePreview) return;
  proceedWithThermalPrint(products, meta, type);
}
